import {
  Button,
  CountryInput,
  DatePicker,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Progress,
  RadioGroup,
  RadioGroupItem,
  SingleFileUploader,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { useForm } from '../../../../../../hooks/useZodForm';
import { passportInfoSchema, TPassportInfoFields } from './schema';

type TPassportInfoFormProps = {
  onNext: (formData: TPassportInfoFields) => void;
  onBack: () => void;
  director?: DirectorBase | null;
  defaultValues?: TPassportInfoFields;
  companyId: number;
};

export const PassportInfoForm = ({
  onNext,
  onBack,
  director,
  defaultValues,
  companyId,
}: TPassportInfoFormProps) => {
  const selectedPassportFile = director?.directorFiles
    ?.sort((a, b) => b.id - a.id)
    .find((eachFile) => eachFile.documentType === LegacyDocumentType.Passport);
  const [passportFile, setPassportFile] =
    useState<FileBasic>(selectedPassportFile);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
  } = useForm<TPassportInfoFields>({
    resolver: zodResolver(passportInfoSchema),
    defaultValues,
  });

  const onSubmit = (formData: TPassportInfoFields) => {
    onNext(formData);
  };

  useEffect(() => {
    if (!passportFile) setValue('passport', null);
    else {
      setValue('passport', passportFile);
      clearErrors('passport');
    }
  }, [passportFile]);

  return (
    <div className="max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader className="px-6 py-5 border-none">
        <DialogTitle>Passport Info</DialogTitle>
      </DialogHeader>
      <div className="flex gap-2">
        <Progress className="rounded-full h-1" accent="success" value={100} />
        <Progress className="rounded-full h-1" accent="default" value={100} />
        <Progress className="rounded-full h-1" />
      </div>
      <div className="grow overflow-hidden">
        <div className="p-6 flex flex-col gap-4 h-full overflow-y-auto">
          <FieldWrapper _className="flex flex-col gap-2">
            <Label className="txt-medium-bold">
              Do you have a UAE Residence Visa?
            </Label>
            <div className="mt-2">
              <RadioGroup
                className="flex flex-col gap-5"
                value={
                  watch('personalDetails.haveUAEResidenceVisa') ? 'yes' : 'no'
                }
                onValueChange={(val) =>
                  setValue(
                    'personalDetails.haveUAEResidenceVisa',
                    val === 'yes'
                  )
                }
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem id="has-translation-yes" value="yes" />
                  <Label htmlFor="has-translation-yes">Yes</Label>
                </div>
                <div className="flex items-center gap-3">
                  <RadioGroupItem id="has-translation-no" value="no" />
                  <Label htmlFor="has-translation-no">No</Label>
                </div>
              </RadioGroup>
            </div>
          </FieldWrapper>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Country of Issue<span className="text-violet-solid ml-1">*</span>
            </Label>

            <CountryInput
              placeholder="Search country"
              type="single"
              onValueChange={(ctr) => {
                setValue('personalDetails.passportCountryOfIssue', ctr);
                clearErrors('personalDetails.passportCountryOfIssue');
              }}
              error={
                errors?.personalDetails?.passportCountryOfIssue?.message &&
                !watch('personalDetails.passportCountryOfIssue')
                  ? errors?.personalDetails?.passportCountryOfIssue?.message
                  : undefined
              }
              value={watch('personalDetails.passportCountryOfIssue')}
              _className="overflow-hidden"
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Number <span className="text-violet-solid ml-1">*</span>
            </Label>

            <Input
              type="text"
              placeholder="Passport number"
              error={errors?.personalDetails?.passportNumber?.message}
              value={watch('personalDetails.passportNumber')}
              onChange={(e) => {
                setValue('personalDetails.passportNumber', e.target.value);
                clearErrors('personalDetails.passportNumber');
              }}
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Expiration date
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <DatePicker
              date={watch('personalDetails.passportExpirationDate')}
              setDate={(dt) => {
                clearErrors('personalDetails.passportExpirationDate');
                setValue('personalDetails.passportExpirationDate', dt);
              }}
              _triggerClassName="min-w-fit w-full"
              hasError={
                !!errors?.personalDetails?.passportExpirationDate?.message
              }
            />
            {errors?.personalDetails?.passportExpirationDate?.message ? (
              <InputErrorMessage
                message={
                  errors?.personalDetails?.passportExpirationDate?.message
                }
              />
            ) : null}
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              Upload Passport
              <span className="text-violet-solid ml-1">*</span>
            </Label>
            <SingleFileUploader
              companyId={companyId}
              setFile={setPassportFile}
              file={passportFile}
              error={errors?.passport?.message}
              setError={(msg: string) =>
                setError('passport', { type: 'custom', message: msg })
              }
              token={undefined}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024}
              documentType={LegacyDocumentType.Passport}
              _className="w-full"
            />
            {errors?.passport?.message ? (
              <InputErrorMessage message="Please upload your passport" />
            ) : null}
          </FieldWrapper>
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onBack}>
          Back
        </Button>
        <Button size="sm" onClick={handleSubmit(onSubmit)}>
          Next
        </Button>
      </DialogFooter>
    </div>
  );
};
